import { useMemo } from 'react';
import type { MapProviderType } from 'src/types/map-provider';
import { mapConfig } from 'src/config/map-config';

/**
 * Hook for getting map provider configuration from environment variables only
 * 
 * This replaces organization-based provider selection with purely environment-based selection.
 * The map provider is determined by:
 * 1. NEXT_PUBLIC_DEFAULT_MAP_PROVIDER environment variable
 * 2. Feature flags for enabling specific providers
 * 3. Available API keys
 * 
 * @returns {MapProviderType | 'auto'} The configured map provider type
 */
export function useEnvMapProvider(): MapProviderType | 'auto' {
  return useMemo(() => {
    
    const configuredProvider = mapConfig.defaultProvider;
    
    switch (configuredProvider) {
      case 'google':
        if (mapConfig.googleMapsApiKey) {
          return 'google';
        }
        console.warn('Google Maps selected but API key not configured, falling back to auto');
        return 'auto';
        
      case 'baidu':
        if (mapConfig.enableBaiduMaps && mapConfig.baiduMapsApiKey) {
          return 'baidu';
        }
        console.warn('Baidu Maps selected but disabled or API key not configured, falling back to auto');
        return 'auto';
        
      case 'openstreetmap':
        if (mapConfig.enableOpenStreetMap) {
          return 'openstreetmap';
        }
        console.warn('OpenStreetMap selected but disabled, falling back to auto');
        return 'auto';
        
      case 'auto':
      default:
        return 'auto';
    }
  }, [mapConfig.defaultProvider]);
}

/**
 * Hook for getting map provider API keys from environment variables
 * 
 * @returns {Partial<Record<MapProviderType, string>>} Available API keys
 */
export function useEnvMapApiKeys(): Partial<Record<MapProviderType, string>> {
  return useMemo(() => {
    const keys: Partial<Record<MapProviderType, string>> = {};
    
    if (mapConfig.googleMapsApiKey) {
      keys.google = mapConfig.googleMapsApiKey;
    }
    
    if (mapConfig.baiduMapsApiKey) {
      keys.baidu = mapConfig.baiduMapsApiKey;
    }
    
    
    
    return keys;
  }, []);
}